import { useState, useEffect, useRef } from "react"
import {
  ChevronDown,
  ChevronRight,
  Calendar,
  Bot,
  X,
  FileText,
  Clock,
  CalendarClock,
  CalendarPlus,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Archive,
  Plus,
  UserCheck,
  Users,
  Gift,
  BadgeCheck,
  MoreVertical,
  Mail,
} from "lucide-react"
import ReactDOM from "react-dom"
import EmailModal from "./EmailModal"
import CandidateDetailsContent from "./job-details/CandidateDetailsContent"
import RescheduleInterviewForm from "./RescheduleInterviewForm"
import ScheduleNextRoundModal from "./ScheduleNextRoundModal"
import InterviewDetailsPage from "./InterviewDetailsPage"

// ── Constants ─────────────────────────────────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"
const GRID_COLS = "minmax(0,1.8fr) minmax(0,1fr) minmax(0,1fr) 210px"
const MAX_ROUNDS = 8

// ── Helpers ───────────────────────────────────────────────────────────────────
const roundLabel = (n) => {
  const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"
  return `${n}${suffix} Interview`
}

const createCandidateContext = ({ candidate, jobListing }) => ({
  candidate: {
    firstName: candidate?.applicantName?.split(" ")[0] ?? null,
    fullName:  candidate?.applicantName ?? null,
  },
  job: {
    role:    jobListing?.role ?? null,
    company: jobListing?.organizationName ?? null,
  },
})

const roundIdToNumber = (roundId = "") => {
  const m = String(roundId).match(/(\d+)/)
  return m ? parseInt(m[1], 10) : 0
}

const normalizeApplicantStage = (candidate) => {
  const raw = (
    candidate.pipelineStage ||
    candidate.applicationStatus ||
    candidate.status ||
    ""
  ).toLowerCase().replace(/[\s_]+/g, "-")
  if (raw.includes("reject"))                         return "rejected"
  if (raw.includes("recruit"))                        return "recruiter-screen"
  if (raw.includes("offer"))                          return "offer"
  if (raw.includes("hire") || raw.includes("hired"))  return "hired"
  const m = raw.match(/(\d+)/)
  if (m) {
    const n = parseInt(m[1], 10)
    if (n >= 1 && n <= MAX_ROUNDS) return `round-${n}`
  }
  return null
}

const isInterviewRecord   = (item) => "scheduledDate" in item || "meetingId" in item
const isFeedbackGenerated = (item) => (item.feedbackStatus || "").toLowerCase() === "generated"

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length]
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()

const fmtDate = (ts) => {
  if (!ts) return "—"
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts)
  return d.toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })
}
const fmtTime = (ts) => {
  if (!ts) return ""
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts)
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
}
const fmtShortDate = (ts) => {
  const d = ts
    ? ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts)
    : new Date()
  const dd = String(d.getDate()).padStart(2, "0")
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const yy = String(d.getFullYear()).slice(-2)
  return `${dd}/${mm}/${yy}`
}

const getSectionKey = (label) => {
  const l = label.toLowerCase()
  if (l.includes("hired"))                              return "hired"
  if (l.includes("archive"))                            return "archived"
  if (l.includes("ai"))                                 return "ai"
  if (l.includes("recruiter") || l.includes("offer"))   return "past"
  return "upcoming"
}

const getSectionIcon = (label, explicitIcon) => {
  if (explicitIcon) return explicitIcon
  const l = label.toLowerCase()
  if (l.includes("recruiter")) return Users
  if (l.includes("offer"))     return Gift
  if (l.includes("hired"))     return BadgeCheck
  if (l.includes("archive"))   return Archive
  if (l.includes("ai"))        return Bot
  if (/\d/.test(l))            return Calendar
  return Calendar
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  border:       "0.5px solid #e5e7eb",
  borderHover:  "0.5px solid #d1d5db",
  radius:       8,
  radiusCard:   10,
  radiusLg:     12,
  bg:           "#fff",
  bgPage:       "#f5f5f0",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd:     "0 4px 16px rgba(0,0,0,0.08)",
}

// ── Status badge ──────────────────────────────────────────────────────────────
const BADGE_STYLES = {
  "Report Ready":      { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
  "Generating…":       { bg: "#eef2ff", color: "#4338ca", border: "#c7d2fe" },
  "Conducted":         { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
  "Cancelled":         { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  "Not Yet Conducted": { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" },
  "Pending Schedule":  { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" },
}

const StatusBadge = ({ interview }) => {
  const fs = (interview.feedbackStatus || "").toLowerCase()
  const st = (interview.status || "").toLowerCase()

  const cfg = (() => {
    if (fs === "generated")  return { label: "Report Ready",      Icon: CheckCircle2, spin: false }
    if (fs === "generating") return { label: "Generating…",       Icon: Loader2,      spin: true  }
    if (st === "completed")  return { label: "Conducted",         Icon: CheckCircle2, spin: false }
    if (st === "cancelled")  return { label: "Cancelled",         Icon: AlertCircle,  spin: false }
    return                          { label: "Not Yet Conducted", Icon: Clock,        spin: false }
  })()

  const s = BADGE_STYLES[cfg.label] || BADGE_STYLES["Not Yet Conducted"]
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", borderRadius: T.radius,
      background: s.bg, color: s.color, border: `0.5px solid ${s.border}`,
      fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
      fontFamily: MONO, whiteSpace: "nowrap",
    }}>
      <cfg.Icon size={10} style={cfg.spin ? { animation: "jii-spin 0.8s linear infinite" } : {}} />
      {cfg.label}
    </span>
  )
}

const PendingBadge = () => {
  const s = BADGE_STYLES["Pending Schedule"]
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", borderRadius: T.radius,
      background: s.bg, color: s.color, border: `0.5px solid ${s.border}`,
      fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
      fontFamily: MONO, whiteSpace: "nowrap",
    }}>
      <Clock size={10} />
      Pending schedule
    </span>
  )
}

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name, size = 32 }) => (
  <div style={{
    width: size, height: size, borderRadius: 7,
    background: getAvatarGradient(name),
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#fff",
    fontSize: size < 36 ? 11 : 13, fontWeight: 700,
    fontFamily: FF, letterSpacing: "0.02em",
  }}>
    {getInitials(name)}
  </div>
)

// ── Action button ─────────────────────────────────────────────────────────────
const ActionBtn = ({ onClick, children, variant = "default", ...rest }) => {
  const variants = {
    default: { background: "#f3f4f6", color: "#374151", border: T.border },
    indigo:  { background: "#4f46e5", color: "#fff",    border: "none"   },
    green:   { background: "#059669", color: "#fff",    border: "none"   },
    blue:    { background: "#2563eb", color: "#fff",    border: "none"   },
    ghost:   { background: "#f9fafb", color: "#374151", border: T.border },
  }
  const s = variants[variant] || variants.default
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        height: 28, padding: "0 10px",
        borderRadius: T.radius - 1, border: s.border,
        background: s.background, color: s.color,
        fontSize: 12, fontWeight: 600, fontFamily: FF,
        letterSpacing: "-0.01em", cursor: "pointer",
        whiteSpace: "nowrap", outline: "none",
        transition: "opacity 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85" }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1" }}
      {...rest}
    >
      {children}
    </button>
  )
}

// ── Icon button (email / three-dot) ──────────────────────────────────────────
const IconBtn = ({ onClick, title, children, className }) => (
  <button
    className={className}
    onClick={onClick}
    title={title}
    style={{
      width: 28, height: 28, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: T.bg, border: T.border,
      borderRadius: T.radius - 1, cursor: "pointer",
      color: T.textTertiary, transition: "all 0.12s", outline: "none",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "#111827"
      e.currentTarget.style.color = "#fff"
      e.currentTarget.style.borderColor = "#111827"
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = T.bg
      e.currentTarget.style.color = T.textTertiary
      e.currentTarget.style.borderColor = "#e5e7eb"
    }}
  >
    {children}
  </button>
)

// ── Dropdown item (dark panel) ────────────────────────────────────────────────
const DropdownItem = ({ icon: Icon, label, color, hoverBg = "#252e40", onClick }) => (
  <button
    className="jii-menu"
    onClick={onClick}
    style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", border: "none",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      background: "transparent", cursor: "pointer",
      fontFamily: FF, transition: "background 0.1s", outline: "none",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
  >
    <Icon size={13} color={color} style={{ flexShrink: 0 }} />
    <span style={{
      fontSize: 12, fontWeight: 700, color,
      letterSpacing: "0.04em", textTransform: "uppercase", fontFamily: MONO,
    }}>
      {label}
    </span>
  </button>
)

// ── Column headers ────────────────────────────────────────────────────────────
const ColHeaders = ({ sectionKey, isSpecialSection }) => (
  <div style={{
    display: "grid", gridTemplateColumns: GRID_COLS,
    padding: "6px 16px", borderBottom: T.border, background: "#fdfcfb",
  }}>
    {[
      "Candidate",
      isSpecialSection ? "" : "Scheduled",
      isSpecialSection ? "" : "Status",
      isSpecialSection
        ? (sectionKey === "hired" ? "Hired on" : "Archived on")
        : "Actions",
    ].map((h, i) => (
      <span key={i} style={{
        fontSize: 12, fontWeight: 700, color: "#6b7280",
        textTransform: "uppercase", letterSpacing: "0.07em",
        fontFamily: MONO, textAlign: i === 3 ? "right" : "left",
      }}>
        {h}
      </span>
    ))}
  </div>
)

// ── Interview row ─────────────────────────────────────────────────────────────
const InterviewRow = ({
  item,
  sectionKey,
  isLast,
  onView,
  onReschedule,
  onScheduleNext,
  onViewReport,
  onArchive,
  onEmailClick,
  onSchedule,
  openDropdownId,
  dropdownPos,
  onOpenMenu,
  onCloseMenu,
}) => {
  const isIV = isInterviewRecord(item)
  const isAI = isIV && (item.interviewMode || "").toLowerCase() === "ai"
  const feedbackGenerated = isIV && isFeedbackGenerated(item)

  const isHiredSection = sectionKey === "hired"
  const isArchivedSection = sectionKey === "archived"
  const isSpecialSection = isHiredSection || isArchivedSection
  // "past" = recruiter-screen / offer applicant records — no three-dot menu
  const isPastSection = sectionKey === "past"
  // three-dot only for interview records outside special/past sections
  const showMenu = isIV && !isSpecialSection && !isPastSection
  // email button: shown for all rows except Hired and Archived
  const showEmail = !isSpecialSection

  const name = isIV ? item.candidateName : item.applicantName
  const sub = isIV
    ? item.candidateEmail || "—"
    : item.personal_information?.currentJobTitle
      ? item.personal_information?.currentCompany
        ? `${item.personal_information.currentJobTitle} · ${item.personal_information.currentCompany}`
        : item.personal_information.currentJobTitle
      : item.personal_information?.currentCompany || "—"

  const specialDate = isHiredSection
    ? fmtShortDate(item.hiredAt)
    : isArchivedSection
      ? fmtShortDate(item.archivedAt)
      : null

  const itemId = item.id || item.meetingId || item.applicantId

  // Resolve email/name for the email modal target
  const emailName = isIV ? item.candidateName : item.applicantName
  const emailAddr = isIV ? item.candidateEmail : item.applicantEmail
  const applicantId = isIV ? item.applicantId : item.applicantId

  return (
    <div
      onClick={() => onView(item)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        padding: "11px 16px",
        alignItems: "center",
        borderBottom: isLast ? "none" : T.border,
        cursor: "pointer",
        transition: "background 0.1s",
        background: "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#fafaf9"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent"
      }}
    >
      {/* Col 1 — Candidate */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
          paddingRight: 12,
        }}
      >
        <Avatar name={name} />
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 600,
              color: isArchivedSection ? T.textMuted : T.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: FF,
            }}
          >
            {name || "—"}
          </p>
          {sub && (
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 12,
                fontWeight: 500,
                color: T.textTertiary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontFamily: FF,
              }}
            >
              {sub}
            </p>
          )}
        </div>
      </div>

      {/* Col 2 — Date */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {!isSpecialSection && isIV ? (
          <>
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: T.text,
                fontFamily: FF,
              }}
            >
              {fmtDate(item.scheduledDate)}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: T.textTertiary,
                marginTop: 2,
                fontFamily: FF,
              }}
            >
              {fmtTime(item.scheduledDate)}
              {item.scheduledEndDate && ` – ${fmtTime(item.scheduledEndDate)}`}
            </span>
          </>
        ) : !isSpecialSection ? (
          <Clock size={14} color={T.textTertiary} />
        ) : null}
      </div>

      {/* Col 3 — Status */}
      <div style={{ display: "flex", alignItems: "center" }}>
        {!isSpecialSection &&
          (isIV ? <StatusBadge interview={item} /> : <PendingBadge />)}
      </div>

      {/* Col 4 — Actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 5,
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isSpecialSection ? (
          /* Hired / Archived — date chip + View only, no email, no menu */
          <>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: T.textMuted,
                fontFamily: FF,
                marginRight: 4,
              }}
            >
              {specialDate}
            </span>
            <ActionBtn variant="ghost" onClick={() => onView(item)}>
              View <ChevronRight size={10} strokeWidth={2.5} />
            </ActionBtn>
          </>
        ) : isPastSection ? (
          /* Recruiter screen / Offer applicant rows — Schedule + email + View */
          <>
            <ActionBtn
              variant="blue"
              onClick={(e) => {
                e.stopPropagation()
                onSchedule(item)
              }}
            >
              <Calendar size={10} strokeWidth={2} />
              Schedule
            </ActionBtn>
            <IconBtn
              title="Send email to candidate"
              onClick={() =>
                onEmailClick({ name: emailName, email: emailAddr, applicantId })
              }
            >
              <Mail size={12} />
            </IconBtn>
            <ActionBtn variant="ghost" onClick={() => onView(item)}>
              View <ChevronRight size={10} strokeWidth={2.5} />
            </ActionBtn>
          </>
        ) : isIV ? (
          /* AI round or numbered-round interview records */
          <>
            {feedbackGenerated && (
              <ActionBtn variant="blue" onClick={() => onViewReport(item)}>
                <FileText size={10} strokeWidth={2} />
                Report
              </ActionBtn>
            )}

            <ActionBtn variant="ghost" onClick={() => onView(item)}>
              View <ChevronRight size={10} strokeWidth={2.5} />
            </ActionBtn>

            {/* Email icon button */}
            <IconBtn
              title="Send email to candidate"
              onClick={() =>
                onEmailClick({ name: emailName, email: emailAddr, applicantId })
              }
            >
              <Mail size={12} />
            </IconBtn>

            {/* Three-dot trigger */}
            <IconBtn
              className="jii-menu"
              title="More options"
              onClick={(e) => onOpenMenu(itemId, e)}
            >
              <MoreVertical size={12} />
            </IconBtn>

            {/* Dropdown portal */}
            {openDropdownId === itemId &&
              dropdownPos &&
              ReactDOM.createPortal(
                <div
                  className="jii-menu"
                  style={{
                    position: "fixed",
                    zIndex: 9999,
                    top: dropdownPos.top,
                    right: dropdownPos.right,
                    width: 192,
                    overflow: "hidden",
                    background: "#1e2535",
                    borderRadius: T.radiusCard,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  }}
                >
                  {isAI ? (
                    feedbackGenerated ? (
                      <>
                        <DropdownItem
                          icon={FileText}
                          label="Report"
                          color="#8b95aa"
                          onClick={() => {
                            onViewReport(item)
                            onCloseMenu()
                          }}
                        />
                        <DropdownItem
                          icon={CalendarPlus}
                          label="Schedule Next"
                          color="#34d399"
                          onClick={() => {
                            onScheduleNext(item)
                            onCloseMenu()
                          }}
                        />
                        <DropdownItem
                          icon={Archive}
                          label="Archive"
                          color="#f87171"
                          hoverBg="#2d1f1f"
                          onClick={() => {
                            onArchive(item)
                            onCloseMenu()
                          }}
                        />
                      </>
                    ) : (
                      <DropdownItem
                        icon={Archive}
                        label="Archive"
                        color="#f87171"
                        hoverBg="#2d1f1f"
                        onClick={() => {
                          onArchive(item)
                          onCloseMenu()
                        }}
                      />
                    )
                  ) : feedbackGenerated ? (
                    <>
                      <DropdownItem
                        icon={CalendarPlus}
                        label="Schedule Next"
                        color="#34d399"
                        onClick={() => {
                          onScheduleNext(item)
                          onCloseMenu()
                        }}
                      />
                      <DropdownItem
                        icon={FileText}
                        label="Report"
                        color="#8b95aa"
                        onClick={() => {
                          onViewReport(item)
                          onCloseMenu()
                        }}
                      />
                      <DropdownItem
                        icon={Archive}
                        label="Archive"
                        color="#f87171"
                        hoverBg="#2d1f1f"
                        onClick={() => {
                          onArchive(item)
                          onCloseMenu()
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <DropdownItem
                        icon={CalendarClock}
                        label="Reschedule"
                        color="#818cf8"
                        onClick={() => {
                          onReschedule(item)
                          onCloseMenu()
                        }}
                      />
                      <DropdownItem
                        icon={Archive}
                        label="Archive"
                        color="#f87171"
                        hoverBg="#2d1f1f"
                        onClick={() => {
                          onArchive(item)
                          onCloseMenu()
                        }}
                      />
                    </>
                  )}
                </div>,
                document.body,
              )}
          </>
        ) : (
          /* Unscheduled applicant inside a round section */
          <>
            <ActionBtn
              variant="blue"
              onClick={(e) => {
                e.stopPropagation()
                onSchedule(item)
              }}
            >
              <Calendar size={10} strokeWidth={2} />
              Schedule
            </ActionBtn>
            <IconBtn
              title="Send email to candidate"
              onClick={() =>
                onEmailClick({ name: emailName, email: emailAddr, applicantId })
              }
            >
              <Mail size={12} />
            </IconBtn>
            <ActionBtn variant="ghost" onClick={() => onView(item)}>
              View <ChevronRight size={10} strokeWidth={2.5} />
            </ActionBtn>
          </>
        )}
      </div>
    </div>
  )
}

// ── Section table ─────────────────────────────────────────────────────────────
const SectionTable = ({
  label,
  icon: iconProp,
  items,
  jobListing,
  defaultCollapsed = false,
  onView,
  onReschedule,
  onScheduleNext,
  onViewReport,
  onArchive,
  onEmailClick,
  onSchedule,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [dropdownPos, setDropdownPos] = useState(null)

  const sectionKey = getSectionKey(label)
  const isSpecialSection = sectionKey === "hired" || sectionKey === "archived"
  const Icon = getSectionIcon(label, iconProp)

  useEffect(() => {
    const onDown = (e) => {
      if (!e.target.closest(".jii-menu")) {
        setOpenDropdownId(null)
        setDropdownPos(null)
      }
    }
    const onScroll = () => {
      setOpenDropdownId(null)
      setDropdownPos(null)
    }
    document.addEventListener("mousedown", onDown)
    window.addEventListener("scroll", onScroll, true)
    return () => {
      document.removeEventListener("mousedown", onDown)
      window.removeEventListener("scroll", onScroll, true)
    }
  }, [])

  const openMenu = (id, e) => {
    e.stopPropagation()
    if (openDropdownId === id) {
      setOpenDropdownId(null)
      setDropdownPos(null)
      return
    }
    const r = e.currentTarget.getBoundingClientRect()
    setDropdownPos({ top: r.bottom + 4, right: window.innerWidth - r.right })
    setOpenDropdownId(id)
  }

  return (
    <div
      style={{
        background: T.bg,
        borderRadius: T.radiusCard,
        border: T.border,
        boxShadow: T.shadow,
        overflow: "visible",
      }}
    >
      {/* Header strip */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "11px 16px",
          background: T.bgMuted,
          border: "none",
          borderBottom: collapsed ? "none" : T.border,
          borderRadius: collapsed
            ? T.radiusCard
            : `${T.radiusCard}px ${T.radiusCard}px 0 0`,
          cursor: "pointer",
          transition: "background 0.12s",
          outline: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#f3f2f0"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = T.bgMuted
        }}
      >
        {collapsed ? (
          <ChevronRight size={13} strokeWidth={2.5} color={T.textTertiary} />
        ) : (
          <ChevronDown size={13} strokeWidth={2.5} color={T.textTertiary} />
        )}
        <Icon size={14} color="#6b7280" style={{ flexShrink: 0 }} />
        <span
          style={{
            flex: 1,
            textAlign: "left",
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            letterSpacing: "-0.01em",
            fontFamily: FF,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#6b7280",
            background: "#f3f4f6",
            border: T.border,
            borderRadius: 5,
            padding: "1px 7px",
            fontFamily: MONO,
          }}
        >
          {items.length}
        </span>
      </button>

      {!collapsed && (
        <>
          <ColHeaders
            sectionKey={sectionKey}
            isSpecialSection={isSpecialSection}
          />
          {items.length === 0 ? (
            <div
              style={{
                padding: "14px 16px",
                fontSize: 13,
                fontWeight: 500,
                color: T.textTertiary,
                fontFamily: FF,
                fontStyle: "italic",
              }}
            >
              No candidates in this stage
            </div>
          ) : (
            items.map((item, idx, arr) => {
              const key = isInterviewRecord(item)
                ? item.id || item.meetingId || idx
                : item.applicantId || idx
              return (
                <InterviewRow
                  key={key}
                  item={item}
                  sectionKey={sectionKey}
                  isLast={idx === arr.length - 1}
                  onView={onView}
                  onReschedule={onReschedule}
                  onScheduleNext={onScheduleNext}
                  onViewReport={onViewReport}
                  onArchive={onArchive}
                  onEmailClick={onEmailClick}
                  onSchedule={onSchedule}
                  openDropdownId={openDropdownId}
                  dropdownPos={dropdownPos}
                  onOpenMenu={openMenu}
                  onCloseMenu={() => {
                    setOpenDropdownId(null)
                    setDropdownPos(null)
                  }}
                />
              )
            })
          )}
        </>
      )}
    </div>
  )
}

// ── Detail panel ──────────────────────────────────────────────────────────────
const DetailPanel = ({
  candidate, jobListing,
  rejectingCandidateId, onRejectCandidate, onScheduleInterview, onMessageCandidate,
  onClose,
}) => {
  useEffect(() => {
    document.body.style.overflow = "hidden"
    const handler = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return (
    <>
      <div
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.28)",
          zIndex: 40, backdropFilter: "blur(1px)",
        }}
        onClick={onClose}
      />
      <div style={{
        position: "fixed", right: 0, top: 0,
        height: "100%", width: "calc(100vw - 18rem)",
        background: T.bg, borderLeft: T.border, zIndex: 50,
        display: "flex", flexDirection: "column",
        boxShadow: "0 0 40px rgba(0,0,0,0.12)", fontFamily: FF,
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "13px 20px", borderBottom: T.border,
          background: T.bgMuted, flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
            <span style={{
              fontSize: 12, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.08em", color: "#6b7280", fontFamily: MONO,
            }}>
              Application
            </span>
            {candidate?.applicantName && (
              <span style={{
                fontSize: 13, fontWeight: 600, color: T.text,
                background: T.bg, border: T.border, borderRadius: 5,
                padding: "2px 9px", fontFamily: FF,
              }}>
                {candidate.applicantName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
              background: T.bg, border: T.border, borderRadius: T.radius - 1,
              cursor: "pointer", color: "#6b7280", transition: "all 0.12s", outline: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#111827"
              e.currentTarget.style.color = "#fff"
              e.currentTarget.style.borderColor = "#111827"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = T.bg
              e.currentTarget.style.color = "#6b7280"
              e.currentTarget.style.borderColor = "#e5e7eb"
            }}
          >
            <X size={13} strokeWidth={2} />
          </button>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <CandidateDetailsContent
            selectedCandidate={candidate}
            jobListing={jobListing}
            rejectingCandidateId={rejectingCandidateId}
            onRejectCandidate={onRejectCandidate}
            onScheduleInterview={onScheduleInterview}
            onMessageCandidate={onMessageCandidate}
          />
        </div>
      </div>
    </>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const JobIdInterviews = ({
  jobListing,
  onSchedule,
  onRejectCandidate,
  rejectingCandidateId,
  interviewers = [],
  organizationId,
  onRefresh,
  onArchiveCandidate,
}) => {
  const [selectedCandidate,     setSelectedCandidate]     = useState(null)
  const [applicationPanelOpen,  setApplicationPanelOpen]  = useState(false)
  const [showReschedule,        setShowReschedule]        = useState(false)
  const [interviewToReschedule, setInterviewToReschedule] = useState(null)
  const [showScheduleNext,      setShowScheduleNext]      = useState(false)
  const [interviewForNext,      setInterviewForNext]      = useState(null)
  const [emailTarget,           setEmailTarget]           = useState(null)

  const [showInterviewDetails, setShowInterviewDetails] = useState(false)
  const [selectedInterviewForDetails, setSelectedInterviewForDetails] =
    useState(null)

  const resolveCandidate = (item) => {
    const applicants = jobListing?.applicants || []
    if (isInterviewRecord(item)) {
      return (
        applicants.find((c) => c.applicantEmail === item.candidateEmail) || null
      )
    }
    return item
  }

  const openApplication = (item) => {
    setSelectedCandidate(resolveCandidate(item))
    setApplicationPanelOpen(true)
  }

  const openInterviewDetails = (item) => {
    if (isInterviewRecord(item)) {
      setSelectedInterviewForDetails(item)
      setShowInterviewDetails(true)
    } else {
      openApplication(item)
    }
  }

  const handleReschedule = (iv) => {
    setInterviewToReschedule(iv)
    setShowReschedule(true)
  }
  const handleScheduleNext = (iv) => {
    setInterviewForNext(iv)
    setShowScheduleNext(true)
  }
  const handleArchive = (item) => {
    onArchiveCandidate?.(item)
  }

  const handleRescheduleComplete = () => {
    setShowReschedule(false)
    setInterviewToReschedule(null)
    onRefresh?.()
  }
  const handleScheduleNextComplete = () => {
    setShowScheduleNext(false)
    setInterviewForNext(null)
    onRefresh?.()
  }

  // Called from any row's email icon — receives { name, email, applicantId }
  const handleEmailClick = ({ name, email, applicantId }) => {
    setEmailTarget({ name, email, applicantId })
  }

  // ── Data bucketing ──────────────────────────────────────────────────────────
  const interviews = jobListing?.interviews || []
  const allApplicants = jobListing?.applicants || []

  const archivedApplicants = allApplicants.filter((c) =>
    (c.applicationStatus || c.status || "").toLowerCase().includes("reject"),
  )
  const activeApplicants = allApplicants.filter(
    (c) =>
      !(c.applicationStatus || c.status || "").toLowerCase().includes("reject"),
  )

  const aiInterviews = interviews.filter(
    (iv) => (iv.interviewMode || "").toLowerCase() === "ai",
  )

  const roundInterviews = {}
  interviews
    .filter((iv) => (iv.interviewMode || "").toLowerCase() !== "ai")
    .forEach((iv) => {
      const n = roundIdToNumber(iv.roundId)
      if (n >= 1 && n <= MAX_ROUNDS) {
        if (!roundInterviews[n]) roundInterviews[n] = []
        roundInterviews[n].push(iv)
      }
    })

  const recruiterApplicants = []
  const offerApplicants = []
  const hiredApplicants = []
  const roundApplicants = {}

  activeApplicants.forEach((c) => {
    const stage = normalizeApplicantStage(c)
    if (!stage || stage === "rejected") return
    if (stage === "recruiter-screen") {
      recruiterApplicants.push(c)
      return
    }
    if (stage === "offer") {
      offerApplicants.push(c)
      return
    }
    if (stage === "hired") {
      hiredApplicants.push(c)
      return
    }
    const m = stage.match(/round-(\d+)/)
    if (m) {
      const n = parseInt(m[1], 10)
      if (!roundApplicants[n]) roundApplicants[n] = []
      roundApplicants[n].push(c)
    }
  })

  const allRoundNumbers = new Set([
    ...Object.keys(roundInterviews).map(Number),
    ...Object.keys(roundApplicants).map(Number),
  ])
  const maxRound = allRoundNumbers.size > 0 ? Math.max(...allRoundNumbers) : 0

  const roundSections = []
  for (let n = maxRound; n >= 1; n--) {
    const ivs = roundInterviews[n] || []
    const apps = roundApplicants[n] || []
    const scheduledIds = new Set(
      ivs.map((iv) => iv.applicantId).filter(Boolean),
    )
    const unscheduledApps = apps.filter((c) => !scheduledIds.has(c.applicantId))
    roundSections.push({
      n,
      items: [...ivs, ...unscheduledApps],
      isEmpty: ivs.length === 0 && unscheduledApps.length === 0,
    })
  }

  const hasAnyContent =
    recruiterApplicants.length > 0 ||
    maxRound > 0 ||
    aiInterviews.length > 0 ||
    offerApplicants.length > 0 ||
    hiredApplicants.length > 0 ||
    archivedApplicants.length > 0

  const sectionProps = {
    jobListing,
    onView: openApplication,
    onSchedule: onSchedule,
    onReschedule: handleReschedule,
    onScheduleNext: handleScheduleNext,
    onViewReport: openInterviewDetails,
    onArchive: handleArchive,
    onEmailClick: handleEmailClick,
  }

   

  return (
    <div
      style={{
        fontFamily: FF,
        background: T.bgPage,
        height: "100%",
        overflowY: "auto",
        scrollbarWidth: "none",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes jii-spin { to { transform: rotate(360deg); } }
        [data-jii-scroll]::-webkit-scrollbar { display: none; }
      `}</style>
      {!hasAnyContent ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 24px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: T.radiusCard,
              border: T.border,
              background: "#f9fafb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
              boxShadow: T.shadow,
            }}
          >
            <Calendar size={18} color={T.textTertiary} />
          </div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: T.text,
              marginBottom: 6,
              fontFamily: FF,
              letterSpacing: "-0.01em",
            }}
          >
            No interviews or pipeline activity yet
          </p>
          <p
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: T.textTertiary,
              marginBottom: 20,
              fontFamily: FF,
            }}
          >
            Schedule your first interview to get started
          </p>
          {onSchedule && (
            <button
              onClick={onSchedule}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                height: 36,
                padding: "0 18px",
                borderRadius: T.radius,
                border: "none",
                background: "#111827",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: FF,
                letterSpacing: "-0.01em",
                cursor: "pointer",
                transition: "background 0.12s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#27272a"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#111827"
              }}
            >
              <Plus size={13} strokeWidth={2.5} />
              Schedule interview
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            padding: "24px 24px 48px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {recruiterApplicants.length > 0 && (
            <SectionTable
              label="Recruiter screen"
              items={recruiterApplicants}
              {...sectionProps}
            />
          )}

          {roundSections.map(({ n, items, isEmpty }) => (
            <SectionTable
              key={`round-${n}`}
              label={roundLabel(n)}
              items={items}
              defaultCollapsed={isEmpty}
              {...sectionProps}
            />
          ))}

          {aiInterviews.length > 0 && (
            <SectionTable
              label="AI round"
              icon={Bot}
              items={aiInterviews}
              {...sectionProps}
            />
          )}

          {offerApplicants.length > 0 && (
            <SectionTable
              label="Offer"
              items={offerApplicants}
              {...sectionProps}
            />
          )}

          {hiredApplicants.length > 0 && (
            <SectionTable
              label="Hired"
              items={hiredApplicants}
              {...sectionProps}
            />
          )}

          <SectionTable
            label="Archived"
            icon={Archive}
            items={archivedApplicants}
            defaultCollapsed
            {...sectionProps}
          />
        </div>
      )}
      {/* Detail panel */}
      {applicationPanelOpen && (
        <DetailPanel
          candidate={selectedCandidate}
          jobListing={jobListing}
          rejectingCandidateId={rejectingCandidateId}
          onRejectCandidate={onRejectCandidate}
          onScheduleInterview={onSchedule}
          onMessageCandidate={(candidate) => {
            const context = createCandidateContext({ candidate, jobListing })
            setEmailTarget({
              name: candidate.applicantName,
              email: candidate.applicantEmail,
              applicantId: candidate.applicantId,
              context,
            })
          }}
          onClose={() => {
            setApplicationPanelOpen(false)
            setSelectedCandidate(null)
          }}
        />
      )}
      {/* Reschedule modal */}
      {showReschedule && interviewToReschedule && (
        <RescheduleInterviewForm
          isOpen={showReschedule}
          onClose={() => {
            setShowReschedule(false)
            setInterviewToReschedule(null)
          }}
          interview={interviewToReschedule}
          onRescheduleComplete={handleRescheduleComplete}
          interviewersData={interviewers}
          orgId={organizationId}
        />
      )}
      {/* Schedule next round modal */}
      {showScheduleNext && interviewForNext && (
        <ScheduleNextRoundModal
          isOpen={showScheduleNext}
          onClose={() => {
            setShowScheduleNext(false)
            setInterviewForNext(null)
          }}
          interview={interviewForNext}
          onScheduleComplete={handleScheduleNextComplete}
          interviewersData={interviewers}
          orgId={organizationId}
        />
      )}

      {showInterviewDetails &&
        selectedInterviewForDetails &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: T.bgPage,
              overflowY: "auto",
            }}
          >
            <InterviewDetailsPage
              interview={selectedInterviewForDetails}
              onBack={() => {
                setShowInterviewDetails(false)
                setSelectedInterviewForDetails(null)
              }}
              onRefreshInterviews={onRefresh}
              interviewers={interviewers}
            />
          </div>,
          document.body,
        )}
      {/* Email modal — opened from row icon or from DetailPanel's message button */}
      {emailTarget && (
        <EmailModal
          isOpen
          onClose={() => setEmailTarget(null)}
          candidateName={emailTarget.name ?? ""}
          candidateEmail={emailTarget.email ?? ""}
          applicantId={emailTarget.applicantId ?? ""}
          organizationId={jobListing?.organizationId ?? ""}
          organizationName={jobListing?.organizationName ?? ""}
          senderId={jobListing?.organizationId ?? ""}
          logoUrl={jobListing?.logoUrl ?? ""}
          context={emailTarget.context ?? null}
        />
      )}
    </div>
  )
}

export default JobIdInterviews